// The interface languages, and how a person's is chosen: their own choice if
// they made one, otherwise the first of their browser's languages that moot
// speaks (any zh* is Traditional Chinese), otherwise English. The browser
// decides from navigator.languages; the Worker, for what it writes itself
// (the sign-in email, the consent page), from the choice cookie and then
// Accept-Language, the same list in the same order.

export type Locale = 'zh-Hant' | 'en'

/** Mirrors the person's choice (kept in their browser) so the Worker can follow it. */
export const LOCALE_COOKIE = 'moot-locale'

export const isLocale = (v: unknown): v is Locale => v === 'zh-Hant' || v === 'en'

/** The first language in `languages` (most preferred first) that moot speaks; English if none. */
export function pickLocale(languages: Iterable<string>): Locale {
  for (const l of languages) {
    const tag = l.trim().toLowerCase()
    if (tag.startsWith('zh')) return 'zh-Hant'
    if (tag.startsWith('en')) return 'en'
  }
  return 'en'
}

/** An Accept-Language header as language tags, most preferred first. */
export function acceptLanguages(header: string | null | undefined): string[] {
  if (!header) return []
  return header
    .split(',')
    .map((part, i) => {
      const [tag = '', ...params] = part.trim().split(';')
      const q = params.map((p) => /^\s*q=([\d.]+)\s*$/.exec(p)?.[1]).find(Boolean)
      return { tag: tag.trim(), q: q === undefined ? 1 : Number(q), i }
    })
    .filter((l) => l.tag && l.tag !== '*' && l.q > 0)
    .sort((a, b) => b.q - a.q || a.i - b.i)
    .map((l) => l.tag)
}

/** The language for something the Worker writes in answer to `request`. */
export function requestLocale(request: Request): Locale {
  const cookie = request.headers.get('cookie') ?? ''
  const chosen = new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=([^;]+)`).exec(cookie)?.[1]
  if (isLocale(chosen)) return chosen
  return pickLocale(acceptLanguages(request.headers.get('accept-language')))
}
